/**
 * A configuração do gráfico trimestral do Fechamento, sem o canvas.
 *
 * Papel: aqui ficam `configuracaoDoGrafico`, que monta o que o Chart.js recebe
 * a partir dos `Trimestres` da API — uma série por categoria na ordem recebida,
 * a cor da categoria ou a de reserva, a borda branca de 1 px, a legenda
 * embaixo, o eixo X sem grade e o Y em reais —, e os três textos da dica, que
 * reproduzem `renderChartTrimestral` (l. 3304–3330).
 *
 * Como usar: é puro e testável sem canvas; quem desenha é o
 * `GraficoTrimestral`, que lê as cores do tema e entrega a configuração ao
 * Chart.js. Mudar um texto da dica aqui muda o que a pessoa lê sob o ponteiro.
 */
import { type Trimestres } from '@concierge/shared';
import { type ChartConfiguration } from 'chart.js';

import { formatarReais } from '../formato';

/** As três cores de token que o canvas usa. */
export interface CoresDoGrafico {
  /** A cor da categoria sem cor no plano: `--emb-cinza-texto`. */
  reserva: string;
  /** A borda branca de cada barra: `--superficie`. */
  borda: string;
  /** As linhas do eixo Y: `--emb-grade-grafico`. */
  grade: string;
}

/** A configuração do gráfico de barras, com os rótulos dos trimestres. */
export type ConfiguracaoDoGrafico = ChartConfiguration<'bar', number[], string>;

/**
 * O título da dica de um trimestre, com os meses somados.
 *
 * @param trimestres - O gráfico do ano.
 * @param indice - A posição do trimestre.
 * @returns `2º Tri (Abr–Mai)`, com os meses só até o mês aberto.
 */
export function tituloDaDica(trimestres: Trimestres, indice: number): string {
  const meses = trimestres.mesesPorTrimestre[indice] ?? [];
  return `${trimestres.rotulos[indice] ?? ''} (${meses.join('–')})`;
}

/**
 * A linha da dica de uma categoria.
 *
 * @param categoria - O nome da série.
 * @param valor - O valor da categoria no trimestre.
 * @returns ` Categoria: R$ 1.234,50`, com o espaço do começo da origem.
 */
export function linhaDaDica(categoria: string, valor: number): string {
  return ` ${categoria}: ${formatarReais(valor)}`;
}

/**
 * O rodapé da dica de um trimestre.
 *
 * @param trimestres - O gráfico do ano.
 * @param indice - A posição do trimestre.
 * @returns `Total: R$ 1.234,50`, somado direto dos meses.
 */
export function rodapeDaDica(trimestres: Trimestres, indice: number): string {
  return `Total: ${formatarReais(trimestres.totais[indice] ?? 0)}`;
}

/**
 * Monta a configuração do Chart.js a partir dos trimestres.
 *
 * @param trimestres - Os rótulos, os meses, os totais e as séries do ano.
 * @param cores - As cores de token lidas do tema.
 * @returns A configuração de `new Chart`.
 */
export function configuracaoDoGrafico(
  trimestres: Trimestres,
  cores: CoresDoGrafico,
): ConfiguracaoDoGrafico {
  return {
    type: 'bar',
    data: {
      labels: trimestres.rotulos,
      datasets: trimestres.series.map((serie) => ({
        label: serie.categoria,
        data: serie.valores,
        backgroundColor: serie.cor ?? cores.reserva,
        borderColor: cores.borda,
        borderWidth: 1,
      })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { font: { size: 10 }, boxWidth: 10, padding: 6 },
        },
        tooltip: {
          callbacks: {
            title: (itens) => tituloDaDica(trimestres, itens[0]?.dataIndex ?? 0),
            label: (contexto) =>
              linhaDaDica(contexto.dataset.label ?? '', contexto.parsed.y ?? 0),
            footer: (itens) => rodapeDaDica(trimestres, itens[0]?.dataIndex ?? 0),
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: {
          ticks: { font: { size: 10 }, callback: (valor) => formatarReais(Number(valor)) },
          grid: { color: cores.grade },
        },
      },
    },
  };
}
